import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import styled from '@emotion/styled';
import { Box, CircularProgress, Switch, Typography, useTheme } from '@mui/material';
import { Button } from '@components/buttons/Button';
import { useStore } from '@stores/RootStore';
import { fonts } from '@theme/fontsCustomer';
import { ChevronIcon } from '@icons/ChevronIcon';
import { useGetSsoProvider, useUpdateSsoProvider, useDeleteSsoProvider } from '@queryHooks/useTenantSsoProvider';
import { SsoProviderEditModal } from './SsoProviderEditModal';
import { StatusBadge } from './SsoProviderStyles';

export const SsoProviderDetailPage = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const params = useParams();
  const tenantId = params.tenantId ?? '';
  const providerId = params.providerId ?? '';

  const {
    uiStore: { alertStore, toastStore },
  } = useStore();

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const { data: provider, isLoading } = useGetSsoProvider(tenantId, providerId);
  const { mutate: updateProvider } = useUpdateSsoProvider();
  const { mutate: deleteProvider } = useDeleteSsoProvider();

  const handleGoBack = () => {
    navigate('../sso-providers');
  };

  const handleToggleStatus = (checked: boolean) => {
    const actionLabel = checked ? t('SsoProvider.toggle.activate') : t('SsoProvider.toggle.deactivate');

    alertStore.open({
      title: actionLabel,
      message: checked ? t('SsoProvider.toggle.activateMessage') : t('SsoProvider.toggle.deactivateMessage'),
      useCancel: true,
      confirmName: actionLabel,
      onConfirm: () => {
        updateProvider(
          { tenantId, providerId, dto: { enabled: checked } },
          {
            onSuccess: () => {
              toastStore.open(
                checked ? t('SsoProvider.toggle.activateSuccess') : t('SsoProvider.toggle.deactivateSuccess'),
                'bottom',
                'center',
              );
            },
            onError: () => {
              toastStore.open(t('SsoProvider.toggle.fail'), 'bottom', 'center');
            },
          },
        );
      },
    });
  };

  const handleDelete = () => {
    alertStore.open({
      type: 'error',
      title: t('SsoProvider.delete.title'),
      message: t('SsoProvider.delete.message'),
      useCancel: true,
      confirmName: t('SsoProvider.delete.confirm'),
      onConfirm: () => {
        deleteProvider(
          { tenantId, providerId },
          {
            onSuccess: () => {
              toastStore.open(t('SsoProvider.delete.success'), 'bottom', 'center');
              navigate('../sso-providers');
            },
            onError: () => {
              toastStore.open(t('SsoProvider.delete.fail'), 'bottom', 'center');
            },
          },
        );
      },
    });
  };

  if (isLoading) {
    return (
      <Container>
        <LoadingBox>
          <CircularProgress size={32} />
        </LoadingBox>
      </Container>
    );
  }

  if (!provider) return null;

  return (
    <Container>
      <GoBackHeader>
        <GoBackButton type="button" onClick={handleGoBack}>
          <ChevronIcon width={24} height={24} rotateNum={-90} color={theme.colors['ic-gray-main']} />
        </GoBackButton>
        <HeaderTitle>{provider.providerName}</HeaderTitle>
      </GoBackHeader>

      <DetailWrapper>
        <InfoRow>
          <InfoLabel>{t('SsoProvider.detail.providerName')}</InfoLabel>
          <InfoValue>{provider.providerName}</InfoValue>
        </InfoRow>
        <InfoRow>
          <InfoLabel>{t('SsoProvider.detail.registrationId')}</InfoLabel>
          <InfoValue>{provider.registrationId}</InfoValue>
        </InfoRow>
        <InfoRow>
          <InfoLabel>{t('SsoProvider.detail.issuerUri')}</InfoLabel>
          <InfoValue>{provider.issuerUri}</InfoValue>
        </InfoRow>
        <InfoRow>
          <InfoLabel>{t('SsoProvider.detail.clientId')}</InfoLabel>
          <InfoValue>{provider.clientId}</InfoValue>
        </InfoRow>
        <InfoRow>
          <InfoLabel>{t('SsoProvider.detail.redirectUri')}</InfoLabel>
          <InfoValue>{provider.redirectUri || '-'}</InfoValue>
        </InfoRow>
        <InfoRow>
          <InfoLabel>{t('SsoProvider.detail.scopes')}</InfoLabel>
          <InfoValue>{provider.scopes}</InfoValue>
        </InfoRow>
        <InfoRow>
          <InfoLabel>{t('SsoProvider.detail.userNameAttribute')}</InfoLabel>
          <InfoValue>{provider.userNameAttribute}</InfoValue>
        </InfoRow>
        <InfoRow>
          <InfoLabel>{t('SsoProvider.detail.userInfoUri')}</InfoLabel>
          <InfoValue>{provider.userInfoUri || '-'}</InfoValue>
        </InfoRow>
        <InfoRow>
          <InfoLabel>{t('SsoProvider.detail.status')}</InfoLabel>
          <StatusRow>
            <Switch checked={provider.enabled} onChange={(_, checked) => handleToggleStatus(checked)} />
            <StatusBadge className={provider.statusColor}>{provider.statusLabel}</StatusBadge>
          </StatusRow>
        </InfoRow>
        <InfoRow>
          <InfoLabel>{t('SsoProvider.detail.createdDate')}</InfoLabel>
          <InfoValue>{provider.createdDate || '-'}</InfoValue>
        </InfoRow>
        <InfoRow>
          <InfoLabel>{t('SsoProvider.detail.updatedDate')}</InfoLabel>
          <InfoValue>{provider.updatedDate || '-'}</InfoValue>
        </InfoRow>
      </DetailWrapper>

      <ActionBox>
        <Button variant="outline" size="small" paddingHorizontal={16} onClick={() => setIsEditModalOpen(true)}>
          {t('SsoProvider.button.edit')}
        </Button>
        <Button variant="outline" size="small" paddingHorizontal={16} onClick={handleDelete}>
          <DeleteText>{t('SsoProvider.button.delete')}</DeleteText>
        </Button>
      </ActionBox>

      <SsoProviderEditModal
        open={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        tenantId={tenantId}
        providerId={providerId}
        provider={provider}
      />
    </Container>
  );
};

const Container = styled(Box)`
  width: 100%;
  padding: 42px 32px 32px 32px;
  height: calc(100vh - var(--manager-gnb-height));
  overflow: auto;
`;

const LoadingBox = styled(Box)`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
`;

const GoBackHeader = styled(Box)`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  margin-bottom: 28px;
`;

const GoBackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
`;

const HeaderTitle = styled(Typography)`
  ${fonts.Headline5}
  color: ${({ theme }) => theme.colors['text-gray-main']};
` as typeof Typography;

const DetailWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 20px 24px 28px;
  background-color: ${({ theme }) => theme.colors['bg-gray-lighter']};
  border: 1px solid ${({ theme }) => theme.colors['border-gray-lighter']};
  border-radius: 5px;
`;

const InfoRow = styled(Box)`
  display: flex;
  flex-direction: row;
  align-items: center;
  min-height: 32px;
`;

const InfoLabel = styled(Typography)`
  ${fonts.Body2};
  color: ${({ theme }) => theme.colors['text-gray-sub-dark']};
  min-width: 180px;
` as typeof Typography;

const InfoValue = styled(Typography)`
  ${fonts.Headline8};
  color: ${({ theme }) => theme.colors['text-gray-main']};
  word-break: break-all;
` as typeof Typography;

const StatusRow = styled(Box)`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

const ActionBox = styled(Box)`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 20px;
`;

const DeleteText = styled.span`
  color: ${({ theme }) => theme.colors['text-red-light']};
`;
